import { useEffect } from 'react';

interface SeoHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  structuredData?: Record<string, unknown>;
  lang?: string;
}

export default function SeoHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  structuredData,
  lang = 'tr',
}: SeoHeadProps) {
  useEffect(() => {
    const originalTitle = document.title;
    document.title = title;

    const setOrCreateMeta = (name: string, content: string, attrName: 'name' | 'property' = 'name') => {
      let el = document.querySelector(`meta[${attrName}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrName, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setOrCreateMeta('description', description);

    setOrCreateMeta('keywords', keywords);

    const canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonicalEl) {
      canonicalEl.setAttribute('href', canonicalUrl);
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = canonicalUrl;
      document.head.appendChild(link);
    }

    setOrCreateMeta('og:title', title, 'property');
    setOrCreateMeta('og:description', description, 'property');
    setOrCreateMeta('og:url', canonicalUrl, 'property');
    setOrCreateMeta('og:type', ogType, 'property');
    if (ogImage) {
      setOrCreateMeta('og:image', ogImage, 'property');
    }

    setOrCreateMeta('twitter:card', 'summary_large_image', 'name');
    setOrCreateMeta('twitter:title', title, 'name');
    setOrCreateMeta('twitter:description', description, 'name');
    if (ogImage) {
      setOrCreateMeta('twitter:image', ogImage, 'name');
    }

    document.documentElement.lang = lang;

    let scriptEl: HTMLScriptElement | null = null;
    if (structuredData) {
      const existingScript = document.querySelector('script[type="application/ld+json"][data-seo]');
      if (existingScript) {
        existingScript.remove();
      }
      scriptEl = document.createElement('script');
      scriptEl.type = 'application/ld+json';
      scriptEl.setAttribute('data-seo', 'true');
      scriptEl.textContent = JSON.stringify(structuredData);
      document.head.appendChild(scriptEl);
    }

    return () => {
      document.title = originalTitle;
    };
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, structuredData, lang]);

  return null;
}